import "./index.css";
import { Ellipsis } from "lucide-react";
import {
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useTranslations } from "../../i18n";
import { IconButton } from "../Button/IconButton";
import { Select } from "../Select";
import type { ISelectOption } from "../Select/types";
import type { ITabsProps } from "./types";
import { useTabsIndicator } from "./useTabsIndicator";
import { useTabsOverflow } from "./useTabsOverflow";
import { clsx } from "clsx";
import { Tag } from "@/ui-kit";

export const Tabs = ({
  items,
  onChange,
  activeItem,
  className,
  variant = "hug",
}: ITabsProps) => {
  const { rtl, t } = useTranslations();

  const tabButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const indicatorRef = useRef<HTMLDivElement | null>(null);
  const underlineRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const activeIndex = useMemo(() => {
    if (!activeItem) return -1;
    return items.findIndex((t) => t.id === activeItem);
  }, [items, activeItem]);

  const { lastVisibleIndex, showMore, hasCalculated } = useTabsOverflow({
    items,
    tabButtonRefs,
    scrollRef,
  });

  const isActiveTabHidden = hasCalculated && activeIndex > lastVisibleIndex;

  useTabsIndicator({
    items,
    activeIndex,
    rtl,
    tabButtonRefs,
    isActiveTabHidden,
    indicatorRef,
    underlineRef,
    scrollRef,
  });

  const getTabVisibility = useCallback(
    (index: number) => {
      const shouldHideInitially = !hasCalculated;
      const isOverflowHidden = hasCalculated && index > lastVisibleIndex;
      return {
        shouldHideInitially,
        isOverflowHidden,
        isHidden: shouldHideInitially || isOverflowHidden,
      };
    },
    [hasCalculated, lastVisibleIndex],
  );

  const handleButtonClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const nextId = event.currentTarget.getAttribute("data-tab-id");
      const isDisabled =
        event.currentTarget.getAttribute("aria-disabled") === "true";
      if (!nextId || isDisabled) return;
      onChange(nextId);
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      // Filter to enabled AND visible tabs
      const enabledAndVisible = items.filter((item, index) => {
        const isDisabled = item.disabled;
        const { isHidden } = getTabVisibility(index);
        return !isDisabled && !isHidden;
      });

      if (enabledAndVisible.length === 0) return;

      const focused = document.activeElement as HTMLElement | null;
      const focusedId = focused?.getAttribute?.("data-tab-id");
      if (!focusedId) return;

      const currentIndex = enabledAndVisible.findIndex(
        (i) => i.id === focusedId,
      );
      if (currentIndex < 0) return;

      const moveFocus = (nextIndex: number) => {
        const nextItem = enabledAndVisible[nextIndex];
        if (!nextItem) return;
        const btn = tabButtonRefs.current.get(nextItem.id);
        btn?.focus();
      };

      switch (event.key) {
        case "ArrowLeft": {
          event.preventDefault();
          const next = rtl
            ? (currentIndex + 1) % enabledAndVisible.length
            : (currentIndex - 1 + enabledAndVisible.length) %
              enabledAndVisible.length;
          moveFocus(next);
          break;
        }
        case "ArrowRight": {
          event.preventDefault();
          const next = rtl
            ? (currentIndex - 1 + enabledAndVisible.length) %
              enabledAndVisible.length
            : (currentIndex + 1) % enabledAndVisible.length;
          moveFocus(next);
          break;
        }
        case "Home":
          event.preventDefault();
          moveFocus(0);
          break;
        case "End":
          event.preventDefault();
          moveFocus(enabledAndVisible.length - 1);
          break;
        case "Enter":
        case " ": {
          event.preventDefault();
          const btn = tabButtonRefs.current.get(focusedId);
          const nextId = btn?.getAttribute("data-tab-id");
          const isDisabled = btn?.getAttribute("aria-disabled") === "true";
          if (nextId && !isDisabled) onChange(nextId);
          break;
        }
        default:
          break;
      }
    },
    [items, onChange, rtl, getTabVisibility],
  );

  const hiddenTabOptions = useMemo((): ISelectOption[] => {
    if (!hasCalculated) return [];

    return items
      .filter((_, index) => index > lastVisibleIndex)
      .map(
        (item): ISelectOption => ({
          id: item.id,
          text: item.text,
          disabled: item.disabled,
        }),
      );
  }, [items, lastVisibleIndex, hasCalculated]);

  const handleMoreTabSelect = useCallback(
    (selectedValue: string | undefined) => {
      if (selectedValue) {
        onChange(selectedValue);
      }
    },
    [onChange],
  );

  const renderMoreAnchor = useCallback(
    () => (
      <IconButton
        variant="ghost"
        color="layer1"
        size="md"
        aria-label={t("ui.tabs.moreTabs")}
        style={{ outlineOffset: -2 }}
      >
        <Ellipsis />
      </IconButton>
    ),
    [t],
  );

  const itemsElements = useMemo(() => {
    const getTabIndex = (
      isActive: boolean,
      isOverflowHidden: boolean,
      index: number,
    ): number => {
      if (isActive && !isOverflowHidden) return 0;
      if (isActiveTabHidden && index === lastVisibleIndex) return 0;
      return -1;
    };

    return items.map((item, index) => {
      const isActive = item.id === activeItem;
      const { isOverflowHidden, isHidden } = getTabVisibility(index);
      const idAttr = `tab-${item.id}`;
      const btnClass = clsx(
        "nuco-tabs__button",
        isActive && "nuco-tabs__button--active",
        `nuco-tabs__button--${variant}`,
        item.disabled && "nuco-tabs__button--disabled",
        isHidden && "nuco-tabs__button--hidden",
        "nuco-typography--body bold",
        item.className,
      );

      return (
        <button
          {...(item.dataAttributes || {})}
          key={item.id}
          id={idAttr}
          type="button"
          ref={(el) => {
            if (el) tabButtonRefs.current.set(item.id, el);
            else tabButtonRefs.current.delete(item.id);
          }}
          role="tab"
          className={btnClass}
          data-tab-id={item.id}
          aria-selected={isActive}
          aria-controls={`panel-${item.id}`}
          aria-disabled={item.disabled ? "true" : "false"}
          tabIndex={getTabIndex(isActive, isOverflowHidden, index)}
          onClick={handleButtonClick}
        >
          {item.text}
          {item.tagProps && <Tag {...item.tagProps} />}
        </button>
      );
    });
  }, [
    variant,
    items,
    isActiveTabHidden,
    lastVisibleIndex,
    activeItem,
    getTabVisibility,
    handleButtonClick,
  ]);

  const rootClass = clsx("nuco-tabs", className);
  const scrollClass = clsx(
    "nuco-tabs__overflow-container",
    `nuco-tabs__overflow-container--${variant}`,
  );

  const content =
    activeIndex >= 0 && items[activeIndex]?.content
      ? items[activeIndex].content
      : null;

  return (
    <div className={rootClass}>
      <div
        ref={scrollRef}
        role="tablist"
        aria-orientation="horizontal"
        className={scrollClass}
        onKeyDown={handleKeyDown}
      >
        {itemsElements}

        {showMore && (
          <div className="nuco-tabs__hidden-select">
            <Select
              options={hiddenTabOptions}
              value={isActiveTabHidden ? activeItem : undefined}
              onChange={handleMoreTabSelect}
              onRenderAnchor={renderMoreAnchor}
              placeholder=""
              placement="bottom-end"
              minWidth={200}
            />
          </div>
        )}

        <div
          ref={underlineRef}
          aria-hidden="true"
          className="nuco-tabs__underline"
        />

        <div
          ref={indicatorRef}
          aria-hidden="true"
          className="nuco-tabs__indicator"
        />
      </div>

      {content && (
        <div
          id={`panel-${items[activeIndex].id}`}
          role="tabpanel"
          aria-labelledby={`tab-${items[activeIndex].id}`}
        >
          {content}
        </div>
      )}
    </div>
  );
};
